import { useState, useEffect } from "react";
import { Building2, ChevronDown, ChevronUp, CreditCard, Calendar, Plus, Loader, RefreshCw, AlertCircle } from "lucide-react";
import { formatCurrencyFull, formatDate, localToday } from "@/lib/data";
import { eventsApi, mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface VendorPayment {
  id: number;
  amount: number;
  payment_date: string;
  paid_at?: string;
  payment_method: string;
  transaction_id?: string;
  notes?: string;
}

interface VendorItem {
  type: 'vendor' | 'service';
  id: number;
  description: string;
  quantity: number;
  cost_price: number;
  total_amount: number;
}

interface VendorSummary {
  vendor_id: number;
  vendor_name: string;
  description?: string;
  total_amount: number;
  paid_amount: number;
  balance: number;
  status?: string;
  payments: VendorPayment[];
  items: VendorItem[];
}

interface PaymentMethod {
  id: number;
  name: string;
  status: string;
}

interface VendorPaymentsSectionProps {
  eventId?: number;
  vendors?: any[];
  onPayClick?: () => void;
  onPayVendor?: (vendor: VendorSummary) => void;
}

export default function VendorPaymentsSection({
  eventId,
  vendors,
  onPayClick,
  onPayVendor,
}: VendorPaymentsSectionProps) {
  const { showToast } = useToast();
  const [expandedVendorId, setExpandedVendorId] = useState<number | null>(null);
  const [vendorsData, setVendorsData] = useState<VendorSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [inlineFormVendorId, setInlineFormVendorId] = useState<number | null>(null);
  const [inlineForm, setInlineForm] = useState({
    amount: "",
    payment_method: "",
    payment_date: localToday(),
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const loadFromApi = async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await eventsApi.getVendorPaymentSummary(eventId);
      const payload = response.data?.data || response.data;
      if (payload?.vendors) {
        const mapped: VendorSummary[] = payload.vendors.map((v: any) => ({
          vendor_id: v.vendor_id,
          vendor_name: v.vendor_name,
          description: v.description || 'Vendor Service',
          total_amount: parseFloat(v.total_amount) || 0,
          paid_amount: parseFloat(v.paid_amount) || 0,
          balance: parseFloat(v.balance) || 0,
          status: v.status || 'pending',
          items: v.items || [],
          payments: (v.payments || []).map((p: any) => ({
            id: p.id,
            amount: parseFloat(p.amount) || 0,
            payment_date: p.payment_date || p.paid_at,
            payment_method: p.payment_method || '',
            transaction_id: p.transaction_id,
            notes: p.notes,
          })),
        })).filter((v: VendorSummary) => v.total_amount > 0 || v.payments.length > 0);
        setVendorsData(mapped);
      } else {
        setVendorsData([]);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load vendor payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      loadFromApi();
    } else if (vendors) {
      const mapped = vendors
        .map((v: any) => ({
          vendor_id: v.vendor_id || v.id,
          vendor_name: v.vendor?.name || v.description || 'Vendor Service',
          description: v.description || 'Vendor Service',
          total_amount: parseFloat(String(v.cost_price || v.unit_price || 0)) * (v.quantity || 1),
          paid_amount: parseFloat(String(v.paid_amount || 0)),
          balance: parseFloat(String(v.cost_price || v.unit_price || 0)) * (v.quantity || 1) - parseFloat(String(v.paid_amount || 0)),
          status: v.status || 'pending',
          items: v.items || [],
          payments: v.payments || [],
        }))
        .filter((v: VendorSummary) => v.total_amount > 0 || v.paid_amount > 0);
      setVendorsData(mapped);
    }
  }, [eventId, vendors]);

  useEffect(() => {
    mastersApi.paymentMethods.list({ status: 'active' })
      .then((res: any) => {
        const methods = res.data?.data || res.data || [];
        setPaymentMethods(methods);
      })
      .catch(() => setPaymentMethods([]));
  }, []);

  const totals = vendorsData.reduce(
    (acc, v) => ({
      total_to_pay: acc.total_to_pay + v.total_amount,
      total_paid: acc.total_paid + v.paid_amount,
      total_balance: acc.total_balance + v.balance,
    }),
    { total_to_pay: 0, total_paid: 0, total_balance: 0 }
  );

  const hasVendors = vendorsData.length > 0;
  const pendingCount = vendorsData.filter((v) => v.balance > 0).length;

  const openInlineForm = (vendorId: number, balance: number) => {
    setInlineFormVendorId(vendorId);
    setInlineForm({
      amount: balance > 0 ? String(balance) : "",
      payment_method: paymentMethods[0]?.name || "",
      payment_date: localToday(),
      notes: "",
    });
  };

  const submitInline = async (vendor: VendorSummary) => {
    if (!eventId) {
      showToast("Cannot record payment without event context", "error");
      return;
    }
    const amount = parseFloat(inlineForm.amount);
    if (!amount || amount <= 0) {
      showToast("Enter a valid amount", "error");
      return;
    }
    if (!inlineForm.payment_method) {
      showToast("Select a payment method", "error");
      return;
    }
    try {
      setSubmitting(true);
      await eventsApi.addVendorPayment(eventId, {
        vendor_id: vendor.vendor_id,
        amount,
        payment_method: inlineForm.payment_method,
        payment_date: inlineForm.payment_date,
        notes: inlineForm.notes,
      });
      showToast("Payment recorded", "success");
      setInlineFormVendorId(null);
      await loadFromApi();
    } catch (err: any) {
      showToast(err?.message || "Failed to record payment", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
      <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100/50 border-b border-blue-100 flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2 flex-wrap">
          <Building2 className="w-4 h-4 text-blue-600" />
          Vendor Payments
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            {vendorsData.length} vendor{vendorsData.length !== 1 ? 's' : ''}
          </span>
          {pendingCount > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
              {pendingCount} pending
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {eventId && (
            <button
              onClick={loadFromApi}
              className="p-1 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
          {onPayClick && hasVendors && (
            <button
              onClick={onPayClick}
              className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
            >
              <CreditCard className="w-3 h-3" />
              Pay All
            </button>
          )}
        </div>
      </div>

      {loading && !hasVendors ? (
        <div className="flex items-center justify-center p-8">
          <Loader className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      ) : error ? (
        <div className="p-4 flex items-center justify-between text-red-700 bg-red-50">
          <div className="flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
          <button onClick={loadFromApi} className="text-xs px-2 py-1 bg-white border border-red-200 rounded">
            Retry
          </button>
        </div>
      ) : !hasVendors ? (
        <div className="p-8 text-center">
          <Building2 className="w-12 h-12 text-stone-200 mx-auto mb-3" />
          <p className="text-stone-400 text-sm">No vendor payments yet</p>
        </div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 text-center">
            <div className="bg-stone-50 rounded-lg p-3">
              <p className="text-[10px] text-stone-500 uppercase tracking-wide mb-1">Total to Pay</p>
              <p className="text-sm font-bold text-stone-800">{formatCurrencyFull(totals.total_to_pay)}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-[10px] text-emerald-600 uppercase tracking-wide mb-1">Paid</p>
              <p className="text-sm font-bold text-emerald-700">{formatCurrencyFull(totals.total_paid)}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-[10px] text-amber-600 uppercase tracking-wide mb-1">Balance</p>
              <p className="text-sm font-bold text-amber-700">{formatCurrencyFull(totals.total_balance)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-[10px] text-blue-600 uppercase tracking-wide mb-1">Pending</p>
              <p className="text-sm font-bold text-blue-700">{pendingCount} / {vendorsData.length}</p>
            </div>
          </div>

          <div className="space-y-2">
            {vendorsData.map((vendor) => {
              const isExpanded = expandedVendorId === vendor.vendor_id;
              const isInlineOpen = inlineFormVendorId === vendor.vendor_id;
              return (
                <div key={vendor.vendor_id} className="border border-stone-200 rounded-lg overflow-hidden">
                  <div
                    className="p-3 bg-stone-50 flex items-center justify-between cursor-pointer hover:bg-stone-100 transition-colors"
                    onClick={() => setExpandedVendorId(isExpanded ? null : vendor.vendor_id)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800 text-sm truncate">{vendor.vendor_name}</p>
                      {vendor.description && (
                        <p className="text-xs text-stone-500 truncate">{vendor.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                      <div className="text-right">
                        <p className="text-xs text-stone-500">Balance</p>
                        <p className={`text-sm font-bold ${vendor.balance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {formatCurrencyFull(vendor.balance)}
                        </p>
                      </div>
                      {onPayVendor && vendor.balance > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onPayVendor(vendor);
                          }}
                          className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
                        >
                          <CreditCard className="w-3 h-3" />
                          Pay
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedVendorId(isExpanded ? null : vendor.vendor_id);
                        }}
                        className="p-1 text-stone-400 hover:text-stone-600"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-3 border-t border-stone-200 bg-white">
                      <div className="grid grid-cols-3 gap-4 mb-3 text-xs">
                        <div>
                          <p className="text-stone-500 mb-1">Total Amount</p>
                          <p className="font-medium text-stone-800">{formatCurrencyFull(vendor.total_amount)}</p>
                        </div>
                        <div>
                          <p className="text-stone-500 mb-1">Paid Amount</p>
                          <p className="font-medium text-emerald-600">{formatCurrencyFull(vendor.paid_amount)}</p>
                        </div>
                        <div>
                          <p className="text-stone-500 mb-1">Status</p>
                          <p className={`font-medium ${vendor.balance === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {vendor.balance === 0 ? 'Paid' : 'Pending'}
                          </p>
                        </div>
                      </div>

                      {vendor.payments && vendor.payments.length > 0 ? (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-stone-600 mb-2 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Payment History ({vendor.payments.length})
                          </p>
                          <div className="space-y-1">
                            {vendor.payments.map((payment: VendorPayment) => (
                              <div key={payment.id} className="flex items-center justify-between text-xs p-2 bg-stone-50 rounded">
                                <div>
                                  <p className="font-medium text-stone-800">{formatCurrencyFull(payment.amount)}</p>
                                  <p className="text-stone-500">
                                    {formatDate(payment.payment_date || payment.paid_at)}
                                    {payment.payment_method && ` • ${payment.payment_method}`}
                                  </p>
                                </div>
                                {payment.transaction_id && (
                                  <span className="text-stone-400">{payment.transaction_id}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 text-center py-2 bg-stone-50 rounded">
                          <p className="text-xs text-stone-400">No payments recorded yet</p>
                        </div>
                      )}

                      {eventId && vendor.balance > 0 && (
                        <div className="mt-3 pt-3 border-t border-stone-100">
                          {!isInlineOpen ? (
                            <button
                              onClick={() => openInlineForm(vendor.vendor_id, vendor.balance)}
                              className="w-full text-xs px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg font-medium transition-colors flex items-center justify-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              Quick add payment record
                            </button>
                          ) : (
                            <div className="space-y-2 bg-blue-50/40 border border-blue-100 rounded-lg p-3">
                              <p className="text-xs font-semibold text-blue-700 uppercase">Record payment</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-[10px] text-stone-500 mb-1">Amount</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={inlineForm.amount}
                                    onChange={(e) => setInlineForm({ ...inlineForm, amount: e.target.value })}
                                    placeholder={String(vendor.balance)}
                                    className="w-full text-xs px-2 py-1.5 border border-stone-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] text-stone-500 mb-1">Date</label>
                                  <input
                                    type="date"
                                    value={inlineForm.payment_date}
                                    onChange={(e) => setInlineForm({ ...inlineForm, payment_date: e.target.value })}
                                    className="w-full text-xs px-2 py-1.5 border border-stone-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-[10px] text-stone-500 mb-1">Payment method</label>
                                  <select
                                    value={inlineForm.payment_method}
                                    onChange={(e) => setInlineForm({ ...inlineForm, payment_method: e.target.value })}
                                    className="w-full text-xs px-2 py-1.5 border border-stone-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    <option value="">Select method...</option>
                                    {paymentMethods.map((m) => (
                                      <option key={m.id} value={m.name}>{m.name}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-[10px] text-stone-500 mb-1">Notes (optional)</label>
                                  <input
                                    type="text"
                                    value={inlineForm.notes}
                                    onChange={(e) => setInlineForm({ ...inlineForm, notes: e.target.value })}
                                    className="w-full text-xs px-2 py-1.5 border border-stone-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2 pt-1">
                                <button
                                  onClick={() => submitInline(vendor)}
                                  disabled={submitting}
                                  className="flex-1 text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded font-medium flex items-center justify-center gap-1"
                                >
                                  {submitting ? <Loader className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                                  Save
                                </button>
                                <button
                                  onClick={() => setInlineFormVendorId(null)}
                                  disabled={submitting}
                                  className="text-xs px-3 py-1.5 bg-white border border-stone-300 hover:bg-stone-50 text-stone-700 rounded font-medium"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
